import { Button } from "@/components/core/buttons/button";
import { invoiceRequestPendingCountQueryOptions } from "@/features/dashboard/api/queries/invoice-request-query";
import { useAuth } from "@/hooks/use-auth";
import {
	IconAffiliate,
	IconBookmark,
	IconBriefcase,
	IconCalendar,
	IconChartBar,
	IconChevronDown,
	IconChevronLeft,
	IconChevronRight,
	IconFileInvoice,
	IconFolderDollar,
	IconHistory,
	IconLayoutDashboard,
	IconLogout,
	IconReceipt,
	IconReport,
	IconReportMoney,
	IconTargetArrow,
	IconTemplate,
	IconTruckDelivery,
	IconUser,
	IconUserCircle,
	IconUserCog,
	IconUsers,
	IconWebhook,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, createFileRoute, redirect, useLocation } from "@tanstack/react-router";
import clsx from "clsx";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_dashboard")({
	beforeLoad: ({ context }) => {
		if (!context.auth.isLoading && !context.auth.user) {
			throw redirect({
				to: "/login",
			});
		}
	},
	component: DashboardLayout,
});

const navItems = [
	{ label: "Dashboard", icon: IconLayoutDashboard, path: "/sales-dashboard", segment: "sales-dashboard" },
	{ label: "Calendar", icon: IconCalendar, path: "/sales-calendar", segment: "sales-calendar" },
	{ label: "Sales", icon: IconReceipt, path: "/sales-contracts", segment: "sales-contracts" },
	{ label: "Collection Report", icon: IconReportMoney, path: "/collection-report", segment: "collection-report" },
	{ label: "Projects", icon: IconBriefcase, path: "/projects", segment: "projects" },
	{ label: "Clients", icon: IconUsers, path: "/clients", segment: "clients" },
	{ label: "Leads", icon: IconTargetArrow, path: "/leads", segment: "leads" },
	{ label: "Leads Dashboard", icon: IconChartBar, path: "/leads-dashboard", segment: "leads-dashboard" },
	{ label: "Invoice Requests", icon: IconFileInvoice, path: "/invoice-requests", segment: "invoice-requests" },
	{ label: "Invoice Print Templates", icon: IconTemplate, path: "/invoice-print-templates", segment: "invoice-print-templates" },
	{ label: "User Profile", icon: IconUserCircle, path: "/user-profile", segment: "user-profile" },
];

const reportNavItems = [{ label: "Sales Contracts", icon: IconReceipt, path: "/report-projects", segment: "report-projects" }];
const bomNavItems = [
	// { label: "Dashboard", icon: IconLayoutDashboard, path: "/", segment: "dashbnoard" },
	{ label: "Supplier Info", icon: IconAffiliate, path: "/bom/supplier-infos", segment: "supplier-infos" }, // IconTruckDelivery // IconBuildingStore // IconAddressBook
	{ label: "Bom Generator", icon: IconFolderDollar, path: "/bom", segment: "bom" },

	// { label: "Project Estimation", icon: IconFolderDollar, path: "/bom", segment: "" },
];

const bottomNavItems = [{ label: "Changelog", icon: IconHistory, path: "/changelog", segment: "changelog" }];

const adminNavItems = [
	{ label: "Users", icon: IconUserCog, path: "/user-approvals", segment: "user-approvals" },
	{ label: "Webhooks", icon: IconWebhook, path: "/webhooks", segment: "webhooks" },
	{ label: "Journal Accounts", icon: IconBookmark, path: "/journal-accounts", segment: "journal-accounts" },
];

function DashboardLayout() {
	const { user, logout, isLoading } = useAuth();
	const { pathname } = useLocation();
	const [collapsed, setCollapsed] = useState(() => {
		return localStorage.getItem("sidebar-collapsed") === "true";
	});

	const toggleSidebar = () => {
		setCollapsed((prev) => {
			localStorage.setItem("sidebar-collapsed", String(!prev));
			return !prev;
		});
	};

	const handleLogout = async () => {
		try {
			await logout();
			toast.success("Logged out successfully");
			window.location.replace("/login");
		} catch (error) {
			toast.error("Failed to logout");
		}
	};

	if (isLoading) {
		return (
			<div className="flex h-screen items-center justify-center bg-background">
				<div className="text-center">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
					<p className="mt-2 text-sm text-gray-600">Loading...</p>
				</div>
			</div>
		);
	}

	const isActive = (segment: string) => {
		const pathSegment = pathname.split("/")[1];
		return segment === pathSegment;
	};

	const isAdmin = user?.roles?.some((role: { name: string }) => role.name === "admin");

	const { data: pendingCount } = useQuery(invoiceRequestPendingCountQueryOptions());

	return (
		<div className="flex h-screen bg-background">
			{/* Sidebar */}
			<aside className={clsx("flex flex-col border-r border-border/50 bg-white transition-all duration-300", collapsed ? "w-16" : "w-64")}>
				{/* Logo area — wide wordmark fills the sidebar when expanded, square crop when collapsed */}
				<div className="flex flex-col items-center justify-center gap-1 px-2 py-3 border-b border-border/50">
					{collapsed ? (
						<img
							src="/salesmon-logo.png"
							alt="SalesMon"
							className="h-10 w-10 rounded object-cover object-left transition-all duration-300"
						/>
					) : (
						<>
							<img src="/salesmon-logo.png" alt="SalesMon" className="h-16 w-full object-contain transition-all duration-300" />
							<span className="text-[10px] font-normal text-foreground/40">v.{__APP_VERSION__}</span>
						</>
					)}
				</div>

				{/* Navigation */}
				<nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
					{navItems.map((item) => (
						<NavItem
							key={item.path}
							item={item}
							collapsed={collapsed}
							active={isActive(item.segment)}
							badge={item.segment === "invoice-requests" && pendingCount ? pendingCount : undefined}
						/>
					))}
					<NavGroup label="BOM" icon={IconFolderDollar} collapsed={collapsed} pathname={pathname} items={bomNavItems} />
					<NavGroup label="Reports" icon={IconReport} collapsed={collapsed} pathname={pathname} items={reportNavItems} />
					{bottomNavItems.map((item) => (
						<NavItem key={item.path} item={item} collapsed={collapsed} active={isActive(item.segment)} />
					))}
					{isAdmin && (
						<>
							<div className="my-2 mx-2 border-t border-border/50" />
							{adminNavItems.map((item) => (
								<NavItem key={item.path} item={item} collapsed={collapsed} active={isActive(item.segment)} />
							))}
						</>
					)}
				</nav>

				{/* User area */}
				<div className="border-t border-border/50 p-3 space-y-2">
					<div className={clsx("flex items-center gap-2", collapsed && "justify-center")} title={user?.email}>
						<IconUser size={18} className="shrink-0 text-foreground/70" />
						{!collapsed && <span className="text-sm text-foreground/70 truncate">{user?.email}</span>}
					</div>
					<div className="flex items-center gap-1">
						<Button onClick={handleLogout} size="sm" variant="ghost" className={clsx("gap-2", collapsed && "w-full justify-center px-0")} title="Logout">
							<IconLogout size={16} />
							{!collapsed && "Logout"}
						</Button>
						<Button
							onClick={toggleSidebar}
							size="sm"
							variant="ghost"
							className={clsx("ml-auto px-2", collapsed && "ml-0 w-full justify-center")}
							title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
						>
							{collapsed ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
						</Button>
					</div>
				</div>
			</aside>

			{/* Main content */}
			<main className="flex-1 overflow-auto">
				<Outlet />
			</main>
		</div>
	);
}

function NavItem({
	item,
	collapsed,
	active,
	badge,
}: {
	item: { label: string; icon: React.ElementType; path: string };
	collapsed: boolean;
	active: boolean;
	badge?: number;
}) {
	const Icon = item.icon;
	return (
		<Link
			to={item.path}
			title={collapsed ? item.label : undefined}
			className={clsx(
				"flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors relative",
				collapsed && "justify-center px-0",
				active ? "bg-primary-50 text-primary-700 border-l-2 border-primary-600" : "text-foreground/70 hover:bg-gray-50 hover:text-foreground",
			)}
		>
			<Icon size={20} className="shrink-0" />
			{!collapsed && <span className="truncate">{item.label}</span>}
			{badge != null && badge > 0 && (
				<span
					className={clsx(
						"inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1",
						collapsed ? "absolute -top-1 -right-1" : "ml-auto",
					)}
				>
					{badge > 99 ? "99+" : badge}
				</span>
			)}
		</Link>
	);
}

function NavGroup({
	label,
	icon: Icon,
	collapsed,
	pathname,
	items,
}: {
	label: string;
	icon: React.ElementType;
	collapsed: boolean;
	pathname: string;
	items: { label: string; icon: React.ElementType; path: string; segment: string }[];
}) {
	const hasActiveChild = items.some((item) => pathname === item.path || pathname.startsWith(item.path + "/"));
	const [open, setOpen] = useState(() => hasActiveChild);

	return (
		<div>
			<button
				type="button"
				onClick={() => setOpen((prev) => !prev)}
				title={collapsed ? label : undefined}
				className={clsx(
					"flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors w-full",
					collapsed && "justify-center px-0",
					hasActiveChild ? "text-primary-700" : "text-foreground/70 hover:bg-gray-50 hover:text-foreground",
				)}
			>
				<Icon size={20} className="shrink-0" />
				{!collapsed && (
					<>
						<span className="truncate flex-1 text-left">{label}</span>
						<IconChevronDown size={16} className={clsx("shrink-0 transition-transform", open && "rotate-180")} />
					</>
				)}
			</button>
			{!collapsed && open && (
				<div className="ml-4 space-y-1">
					{items.map((item) => {
						const isActive = pathname === item.path;
						const ItemIcon = item.icon;
						return (
							<Link
								key={item.path}
								to={item.path}
								className={clsx(
									"flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
									isActive ? "bg-primary-50 text-primary-700 border-l-2 border-primary-600" : "text-foreground/70 hover:bg-gray-50 hover:text-foreground",
								)}
							>
								<ItemIcon size={18} className="shrink-0" />
								<span className="truncate">{item.label}</span>
							</Link>
						);
					})}
				</div>
			)}
		</div>
	);
}
